import type { CustomerAddress } from './Customer'
import type { EmployeePresence, EmployeePresenceState, EmployeeViewOtherPartyOverride } from './Employee'
import type { PbxCalendarEventReduced } from './PbxCalendar'
import type { PbxCallState } from './PbxCommon'

// #region PBX App Settings

export enum PbxAppSettingsDesktopResponseKuandoModes {
	/** Solid color */
	COLOR = 'COLOR',
	/** Two color fast at 1,1 */
	TWO_COLOR_FAST = 'TWO_COLOR_FAST',
	/** Two color normal at 10,10 */
	TWO_COLOR_NORMAL = 'TWO_COLOR_NORMAL',
	/** Two color slow at 20,20 */
	TWO_COLOR_SLOW = 'TWO_COLOR_SLOW',
	/** Blink at 1,1 */
	BLINK_EMERGENCY = 'BLINK_EMERGENCY',
	/** Blink at 5,5 */
	BLINK_FAST = 'BLINK_FAST',
	/** Blink at 10,10 */
	BLINK_NORMAL = 'BLINK_NORMAL',
	/** Blink at 20,20 */
	BLINK_SLOW = 'BLINK_SLOW',
	/** Intense pulse at 33, 1 */
	PULSE_INTENSE = 'PULSE_INTENSE',
	/** Normal pulse at 20, 1 */
	PULSE_NORMAL = 'PULSE_NORMAL',
	/** Soft pulse at 10, 1 */
	PULSE_SOFT = 'PULSE_SOFT',
	/** Disco mode */
	DISCO = 'DISCO',
	/** Sequence with duration 5,1  */
	FLASH_FAST = 'FLASH_FAST',
	/** Sequence with duration 20,1  */
	FLASH_SLOW = 'FLASH_SLOW'
}
export interface PbxAppSettingsDesktopResponseKuandoColor {
	mode: PbxAppSettingsDesktopResponseKuandoModes
	color?: {
		r: number
		g: number
		b: number
	} | null
	altColor?: {
		r: number
		g: number
		b: number
	} | null
}

/** Response for /pbx/app/settings/desktop */
export interface PbxAppSettingsDesktopResponse {
	/** Ringtone to use for external callers, null for no ringtone. */
	ringtoneExternal: string | null

	/** Ringtone to use for internal callers, null for no ringtone. */
	ringtoneLocal: string | null

	/** Ringtone to use for queue calls, null for regular ringtones */
	ringtoneQueue: string | null

	/** True if the status panel should be shown */
	showStatusPanel: boolean

	/** True if the application should auto launch on user login */
	autoLaunch: boolean

	/** True if the application should auto launch and stay hidden */
	autoLaunchHide: boolean

	/** True to play an alarm when a queue caller has waiting for a critical long time */
	queueCriticalSound: boolean

	/** Name of skin for styling */
	theme: string

	/** Id of phone to control */
	phoneId: string | null

	/** Id of softphone device when used - updated by sipClient  */
	softphoneId: string | null

	/** Id of dashboard to show */
	dashboardId: string | null

	/** True if we are in wallboard mode */
	windowWallboardMode: boolean

	/** True if presence shall also color the background of the employee */
	presenceBackground: boolean

	/** True if the background color of employees shall also change if employee is on a call */
	callBackground: boolean

	/** True if we are in/shall run in - fullscreeen mode */
	fullscreen: boolean

	/** When true Communicator sets itself DND while the computer is locked. */
	dndWhenDesktopLocked: boolean

	/** When true and Communicator is minimized or not in focus, it will pop to the front. */
	popupOnIncomingCall: boolean

	/** If popupOnIncomingCall is enabled, this will hide Communicator when the call has ended. */
	popupRevertWhenDone: boolean

	/** True if Communicator shall always stay on top. */
	alwaysOnTop: boolean

	/** Position and size of the window */
	window: {
		x: number
		y: number
		width: number
		height: number
	}

	/** UI Zoom level */
	zoom: number

	/** Standard width of columns in the dashboard (default 370) */
	dashboardColumnWidth: number

	/** Global shortcuts settings */
	globalShortcuts: PbxAppSettingsDesktopGlobalShortcuts

	/** Integration settings */
	integration: PbxAppSettingsDesktopIntegration

	/** Media settings - per instance, i.e. per machine the app runs on */
	media: {
		/** Raw device labels of the preferred microphones, most recently chosen first, latest 10 */
		preferredAudioInputs: string[]
		/** Raw device labels of the preferred speakers, most recently chosen first, latest 10 */
		preferredAudioOutputs: string[]
		/** Raw device labels of the preferred cameras, most recently chosen first, latest 10 */
		preferredVideoInputs: string[]
		/** Raw device labels of the preferred speakers for playing the ringtone on, most recently chosen first, latest 10 */
		preferredRingerOutputs: string[]
		/** Ringtone volume: 0-1 */
		ringerVolume: number
		/** Settings for Kuando busylight */
		kuando: {
			/** True to use it */
			use: boolean
			/** Id of ringtone to use */
			ringTone: number
			/** Ringtone volume: 0-100 */
			ringVolume: number
			/** True to use custom color settings */
			useColorSettings: boolean
			/** Custom settings for Kuando colors */
			colorSettings?: {
				idle: PbxAppSettingsDesktopResponseKuandoColor
				ringing: PbxAppSettingsDesktopResponseKuandoColor
				calling: PbxAppSettingsDesktopResponseKuandoColor
				busy: PbxAppSettingsDesktopResponseKuandoColor
				desktopLocked: PbxAppSettingsDesktopResponseKuandoColor
				presenceBusy: PbxAppSettingsDesktopResponseKuandoColor
				presenceOutOfOffice: PbxAppSettingsDesktopResponseKuandoColor
				presenceOffWork: PbxAppSettingsDesktopResponseKuandoColor
				presenceHoliday: PbxAppSettingsDesktopResponseKuandoColor
			} | null
		}
	}

	/** List of queues with distinct ringtones. */
	queueRingtones: PbxAppSettingsQueueRingtone[]

	/** True if outbound goto is enabled and caller id cannot be controlled by the user */
	outboundGoto: boolean

	/** True if beacons may update the users location. */
	beaconsMayUpdateLocation: boolean
}

export interface PbxAppSettingsQueueRingtone {
	/** Id of queue */
	queue: string
	/** Id of ringtone */
	ringtone: string
}

export interface PbxAppSettingsDesktopIntegration {
	/** Run application */
	run: PbxAppSettingsDesktopIntegrationRun
	/** Send keystrokes */
	sendkeys: PbxAppSettingsDesktopIntegrationSendkeys
	/** Copy data to clipboard for later pasting */
	copyToClipboard: PbxAppSettingsDesktopIntegrationCopyToClipboard
	/** Control a browser using the browser integration */
	browser: PbxAppSettingsDesktopIntegrationBrowser
	/** Open a website */
	website: PbxAppSettingsDesktopIntegrationWebsite
}

export interface PbxAppSettingsDesktopIntegrationRun {
	/** When to invoke */
	when: PbxAppSettingsIntegrationRunWhen
	/** Delay x milliseconds before invoking */
	delay?: number
	/** The command to run */
	command?: string
	/** Shall the integration only run if all args has values */
	requireArgs?: boolean
}

export interface PbxAppSettingsDesktopIntegrationSendkeys {
	/** When to invoke */
	when: PbxAppSettingsIntegrationRunWhen
	/** Delay x milliseconds before invoking */
	delay?: number
	/** Name of process to give forcus to */
	focusProcess?: string
	/** Shall the process be maximized */
	maximizeProcess?: boolean
	/** Use AutoIT instead of system to sending keystrokes */
	useAutoit?: boolean
	/** String to key out */
	keys?: string
	/** Shall the integration only run if all args has values */
	requireArgs?: boolean
}

export interface PbxAppSettingsDesktopIntegrationCopyToClipboard {
	/** When to invoke */
	when: PbxAppSettingsIntegrationRunWhen
	/** The string to store in the clipboard */
	what?: string
	/** Shall the integration only run if all args has values */
	requireArgs?: boolean
}

export interface PbxAppSettingsDesktopIntegrationBrowser {
	/** When to invoke */
	when: PbxAppSettingsIntegrationRunWhen
	/** hostname of website to be active on */
	website?: string
	/** True if target website is a single-page-application */
	spa?: boolean
	/** List of actions to perform */
	actions?: PbxAppSettingsDesktopIntegrationBrowserAction[]
	/** Shall the integration only run if all args has values */
	requireArgs?: boolean
}

export interface PbxAppSettingsDesktopIntegrationWebsite {
	/** When to invoke */
	when: PbxAppSettingsIntegrationRunWhen
	/** Number of seconds to wait before invoking the integration */
	delay: number
	/** URL to open, supports variables. */
	url?: string
	/** True if a window is preferred over a tab */
	preferWindow: boolean
	/** Shall the integration only run if all args has values */
	requireArgs?: boolean
	/** If set the integration runs headless (no UI) and controls the type of request to make */
	apiMode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | null
	/** If apiMode is POST, PUT or PATCH this contains the body. Supports variables. */
	apiData?: string | null
}

/** Request for /pbx/app/settings/desktop */
export type PbxAppSettingsDesktopRequest = Partial<PbxAppSettingsDesktopResponse>

/** Part of PbxAppSettingsRequest, PbxAppSettingsResponse */
export interface PbxAppSettingsDesktopGlobalShortcuts {
	ANSWER: PbxAppSettingsDesktopResponseGlobalShortcut
	HANGUP: PbxAppSettingsDesktopResponseGlobalShortcut
	TOGGLE_WINDOW: PbxAppSettingsDesktopResponseGlobalShortcut
	CALL: PbxAppSettingsDesktopResponseGlobalShortcut
	SEARCH: PbxAppSettingsDesktopResponseGlobalShortcut
}

/** Part of PbxAppSettingsRequest, PbxAppSettingsResponse */
export interface PbxAppSettingsDesktopResponseGlobalShortcut {
	key: string
	commandOrControl: boolean
	shift: boolean
	alt: boolean
	enabled: boolean
}

/** Part of PbxAppSettingsRequest, PbxAppSettingsResponse */
export interface PbxAppSettingsDesktopIntegrationBrowserAction {
	action: PbxAppSettingsIntegrationBrowserActionType
	value?: number | string // sleep=number, target=string, insert=string, js=string
}

export type PbxAppSettingsDesktopRingtoneResponse = PbxAppSettingsDesktopRingtoneResponseItem[]

export interface PbxAppSettingsDesktopRingtoneResponseItem {
	/** Id of the ringtone - also audio server file id */
	_id: string
	/** Name in english */
	nameEn: string
	/** Name in danish */
	nameDa: string
}

export type PbxAppSettingsDesktopThemeResponse = PbxAppSettingsDesktopThemeResponseItem[]

export interface PbxAppSettingsDesktopThemeResponseItem {
	/** Id of the theme */
	_id: string
	/** Name in english */
	nameEn: string
	/** Name in danish */
	nameDa: string
}

// #endregion

// #region PBX App Dashboard

/** Response for /pbx/app/dashboard */
export type PbxAppDashboardListResponse = PbxAppDashboardListResponseItem[]

/** Part of PbxAppDashboardListResponse */
export interface PbxAppDashboardListResponseItem {
	/** Unique id of the dashboard */
	_id: string
	/** Name of the dashboard */
	name: string
	/** Priority in the topbar, 0=do not display in topbar, 1-5=positional priority */
	topbar: number
	/** True if dashboard is a shared dashboard */
	shared: boolean
}

/** Response for /pbx/app/dashboard/?customer=ObjectId */
export type PbxAppDashboardListForCustomerResponse = PbxAppDashboardListForCustomerResponseItem[]

/** Part of PbxAppDashboardListForCustomerResponse */
export interface PbxAppDashboardListForCustomerResponseItem {
	/** Unique id of the employee the dashboards belongs to, null for shared dashboards */
	_id?: string
	/** Name of the employee the dashboards belongs to, 'Shared' for shared dashboards */
	name: string
	/** True if this is a list of shared dashboards, false if they belongs to an employee */
	shared: boolean
	/** List of dashboards */
	dashboards: PbxAppDashboardListForCustomerResponseItemDashboard[]
}

/** Part of PbxAppDashboardListForCustomerResponse */
export interface PbxAppDashboardListForCustomerResponseItemDashboard {
	/** Unique id of the dashboard */
	_id: string
	/** Name of the dashboard */
	name: string
	/** Priority in the topbar, 0=do not display in topbar, 1-5=positional priority */
	topbar: number
}

/** Response for /pbx/app/dashboard/{id} */
export interface PbxAppDashboardResponse {
	/** Id of dashboard */
	_id: string
	/** Id of customer */
	customer: string
	/** Id of employee the dashboard belongs to, null if dashboard is shared. */
	employee?: string
	/** Optional id of a PBX group, the employees extension must be a member of to view this dashboard. Null when not shared. */
	group?: string
	/** True if dashboard is shared. */
	shared: boolean
	/** Name of the dashboard. */
	name: string
	/** Priority in the topbar, 0=do not display in topbar, 1-5 positional priority. */
	topbar: number
	/** List of items on the dashboard. */
	items: PbxAppDashboardItem[]
}

/** Request for /pbx/app/dashboard */
export type PbxAppDashboardRequest = Omit<PbxAppDashboardResponse, '_id'>

/** Request for /pbx/app/dashboard/{id} */
export type PbxAppDashboardUpdateRequest = Partial<Omit<PbxAppDashboardRequest, 'customer' | 'employee' | 'shared'>>

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export type PbxAppDashboardItem = PbxAppDashboardItemMyCalls | PbxAppDashboardItemQueue | PbxAppDashboardItemMultiQueue | PbxAppDashboardItemExtensions | PbxAppDashboardItemWeb | PbxAppDashboardItemSwitchVar | PbxAppDashboardItemShortcuts

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemCommon {
	/** 0-based index of column the item belongs to. */
	column: number
	/** 0-based index of the position of the item in the column. */
	position: number
	/** Type of item: MYCALLS, QUEUE, EXTENSIONS, WEB, SWITCHVAR, SHORTCUTS */
	type?: PbxAppDashboardItemType

	/** Used for UI binding/ref - added locally */
	tag?: string
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemMyCalls extends PbxAppDashboardItemCommon {
	/** Max number of calls to show before scrolling. */
	maxCalls: number
	/** Optional name of variable to show with the call data. */
	variable?: string
	/** Optional label to prefix the variable with. */
	variableName?: string
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemQueue extends PbxAppDashboardItemCommon {
	/** Id of queue to show. */
	_id: string
	/** * Optional name of variable to show with the call data. */
	variable?: string
	/** Optional label to prefix the variable with. */
	variableName?: string
	/** True to show calls waiting in the queue. */
	showCalls: boolean
	/** True to show members of the queue. */
	showMembers: boolean
	/** True to show mini queue stats for the day. */
	showStats: boolean
	/** Max number of calls to show before scrolling. */
	maxCalls: number
	/** Max number of members to show before scrolling. */
	maxMembers: number
	/** List of statistics properties to show: NOMEMBERS, ALLBUSY, TIMEOUT, CANCELLED, ANSWERED, UNANSWERED, TOTAL, AVERAGEWAITTIME, AVERAGETALKTIME, TOTALTALKTIME, LONGESTWAITTIME, LASTWAITTIME. */
	stats: PbxAppDashboardItemQueueStats[]
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemMultiQueue extends PbxAppDashboardItemCommon {
	/** Panel title */
	title: string
	/** Array of queue IDs to show. */
	_ids: string[]
	/** Optional name of variable to show with the call data. */
	variable?: string
	/** Optional label to prefix the variable with. */
	variableName?: string
	/** True to show calls waiting in the queues. */
	showCalls: boolean
	/** True to show members of the queues. */
	showMembers: boolean
	/** True to show mini queue stats for the day. */
	showStats: boolean
	/** Max number of calls to show before scrolling. */
	maxCalls: number
	/** Max number of members to show before scrolling. */
	maxMembers: number
	/** List of statistics properties to show: NOMEMBERS, ALLBUSY, TIMEOUT, CANCELLED, ANSWERED, UNANSWERED, TOTAL, AVERAGEWAITTIME, AVERAGETALKTIME, TOTALTALKTIME, LONGESTWAITTIME, LASTWAITTIME. */
	stats: PbxAppDashboardItemQueueStats[]
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemExtensions extends PbxAppDashboardItemCommon {
	/** Title for the panel. */
	title: string
	/** Array of ids of extensions to list, in order. */
	extensions: string[]
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemWeb extends PbxAppDashboardItemCommon {
	/** Width in pixels. Column will resize to the widest web content in the column. */
	width: number
	/** Height in pixels. */
	height: number
	/** Title for the panel. */
	title: string
	/** Mode  */
	mode: PbxAppDashboardItemWebMode
	/** When mode is RELOADED, this is the number of seconds between reloading the content. */
	interval: number
	/** URL to invoke, including query args that may contain %%A_NUMBER%%, %%B_NUMBER%%, %%A_NAME%%, %%B_NAME%%, %%VARIABLE%%. */
	url: string
	/** Close method */
	close: PbxAppDashboardItemWebClose
	/** If mode is RINGING or ANSWERED and this is true, then the content will not be triggered when caller is local. */
	ignoreLocal: boolean
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemShortcuts extends PbxAppDashboardItemCommon {
	/** Title for panel. */
	title: string
	shortcuts: PbxAppDashboardItemShortcutsItem[]
}

export interface PbxAppDashboardItemShortcutsItem {
	/** Label for the shortcut. */
	name: string
	/** Number to call. */
	number: string
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemSwitchVar extends PbxAppDashboardItemCommon {
	/** Name of content - for headline. */
	title: string
	variables: PbxAppDashboardItemSwitchVarVariable[]
}

/** Part of PbxAppDashboardRequest, PbxAppDashboardResponse */
export interface PbxAppDashboardItemSwitchVarVariable {
	/** Type */
	type: 'SWITCH' | 'VARIABLE'
	/** Switch or variable name. */
	name: string
	/** Ownership: PERSONAL, SHARED, SYSTEM (required MANAGER level or higher). */
	owner: 'PERSONAL' | 'SHARED' | 'SYSTEM'
	/** Label for the switch or variable. */
	label: string
	/** Optional list of string that can be selected from a dropdown list and assigned. Variable types only. */
	values?: string[]
}

// #endregion

// #region PBX App Employees

/** Request for /pbx/app/employees */
export interface PbxAppEmployeesRequest {
	/** Optional ids of employees to limit the result to */
	ids?: string[]
	/** True to include list of current calls on the members extensions. But only if the user is permitted to see them via the group permission viewOtherParty. */
	calls?: boolean
	/** True to also include extensions which are not linked to an employee. */
	standalone?: boolean
}

export type PbxAppEmployeesResponse = PbxAppEmployeesItem[]

/** Response for /pbx/app/employees */
export interface PbxAppEmployeesItem {
	/** Unique id of employee, or extension (if standalone=true). */
	_id: string
	/** Type of record: EMPLOYEE, EXTENSION. */
	type: PbxAppEmployeesType
	/** employee name or extension name. */
	name: string
	/** The employees title (type EMPLOYEE only). */
	title?: string
	/** The employees department/section/group (type EMPLOYEE only). */
	department?: string
	/** E-mail address (also used for login) (type EMPLOYEE only). */
	emailAddress?: string
	/** Date of birth, null if not set (type EMPLOYEE only). */
	birthdate?: string
	/** Id of a picture of the employee, null if no picture is set (type EMPLOYEE only). */
	picture?: string
	/** Override for PBX group access to see who the employee is talking to (type EMPLOYEE only): */
	viewOtherPartyOverride?: EmployeeViewOtherPartyOverride
	/** Address data (type EMPLOYEE only). */
	address?: CustomerAddress
	/** Presence settings (type EMPLOYEE only) */
	presence?: PbxAppEmployeesPresence
	/** List of extensions. For type EXTENSION this always holds 1 item. */
	extensions: PbxAppEmployeesExtension[]
	/** List of calendar events, if MS365 integration is enabled. */
	calendar?: PbxAppEmployeesCalendar[]
}

export type PbxAppEmployeesCalendar = Omit<PbxCalendarEventReduced, 'employee'>

export interface PbxAppEmployeesExtension {
	/** Id of extension. */
	_id: string
	/** Extensions local number. */
	number?: string
	/** Direct phone number. */
	direct?: string
	/** List of mobile phone numbers that belongs to this extension. E.164 format. */
	mobile: string[]
	/** True if this is the primary extension for the employee. */
	primary?: boolean
	/** Current state of the extension: IDLE, RINGING, BUSY - CALLING is computed locally here. */
	state: 'IDLE' | 'RINGING' | 'BUSY' | 'CALLING'
	/** Number to redirect to when extension is called. Used to quickly circumvent the extension. */
	redirect?: string
	/** List of calls the extension currently has. */
	calls: PbxAppEmployeesExtensionCall[]
	/** List of phones - only populated if the employee is the requesting user (e.g. you can see your own phones). */
	phones?: PbxAppEmployeesExtensionPhone[]
	/** Group permissions for the extension. */
	permissions: PbxAppGroupPermissionsResponse
	/** Number of calls waiting in personal queue */
	personalQueue: number
}

export interface PbxAppEmployeesExtensionWithDate extends Omit<PbxAppEmployeesExtension, 'calls'> {
	/** List of calls the extension currently has. */
	calls: PbxAppEmployeesExtensionCallWithDate[]
}

/** The class of the other party's device on a call - published by the PBX only when both ends can do video */
export type PbxPeerVideo = 'desktop' | 'mobile'

/**
 * What the PBX adds to each leg's CALL_ANSWER and CALL_UPDATE events for video - the realtime client's PbxEventCall
 * doesn't carry it yet
 */
export interface PbxEventCallVideo {
	/** The other party's device class, present only when both this leg's device and the other party's can do video. */
	peerVideo?: PbxPeerVideo
}

export interface PbxAppEmployeesExtensionCall {
	/** Unique ID of the call - <pbx-hostname>:<channel-id>. */
	_id: string
	/** Id of customer. */
	customer: string
	/** Id of extension the call belongs to. */
	extension: string
	/** Call state: CALL_START, CALL_RING, CALL_ANSWER, CALL_HOLD, CALL_UNHOLD, CALL_END, CALL_UPDATE. */
	state: PbxCallState
	/** When the call started. */
	started?: string
	/** When the call started ringing. */
	ringing?: string
	/** When the call was answered. */
	answered?: string
	/** Caller data. */
	caller: PbxAppEmployeesExtensionCallCaller
	/** Callee data. */
	callee: PbxAppEmployeesExtensionCallCallee
	/** Session ID for the call (to match up inbound and outbound legs of the same call). */
	sessionId: string
	/** Unique channel ID for this call leg. */
	channel: string
	/** SIP call ID, if available. */
	callId?: string
	/** I for inbound call received from the outside, O for outbound to the outside. */
	direction: 'I' | 'O'
	/** I for inbound towards the PBX, O outbound from the PBX. */
	leg: 'I' | 'O'
	/** Type of call originator: SIPPHONE, MOBILE, EXTERNAL, SYSTEM. */
	originator: PbxAppEmployeesExtensionCallOriginator
	/** Id of the device that the extension is using, if available. */
	device?: string
	/** Hostname of the PBX server handling the call. */
	pbx: string
	/** Unique channel ID of caller when call is received. */
	callerChannelId?: string
	/** Extension of the party that initiated the hold. Only set on CALL_HOLD / CALL_UNHOLD. */
	holdOwnerExtension?: string
	/** Device of the party that initiated the hold. Only set on CALL_HOLD / CALL_UNHOLD. */
	holdOwnerDevice?: string
	/** The other party's device class when both ends can do video - from the latest CALL_ANSWER or CALL_UPDATE. */
	peerVideo?: PbxPeerVideo

	/** When call was put on hold - set locally. */
	hold?: Date
	/** When call ended - set locally */
	ended?: Date
	/** True if other party shall be hidden - set locally by employees->parseCall */
	hideOtherParty: boolean
}

/**
 * Employee extension call with dates as Date objects.
 */
export interface PbxAppEmployeesExtensionCallWithDate extends Omit<PbxAppEmployeesExtensionCall, 'started' | 'ringing' | 'answered'> {
	/** When the call started. */
	started?: Date
	/** When the call started ringing. */
	ringing?: Date
	/** When the call was answered. */
	answered?: Date
	/** If this is a held call, this will be true if the user put the call on hold, and false if not. And undefined if call is not on hold */
	iOwnHold?: boolean
	/** Variables associated with the call */
	variables: Record<string, string>
}

export interface PbxAppEmployeesExtensionCallCaller {
	/** Callers phone number */
	number: string
	/** Callers name, if available. */
	name?: string
	/** True if caller has secret number. */
	secret: boolean
	/** Id of queue, if call was initiated by a queue. */
	queueId?: string
	/** Name of queue, if call was initiated by a queue. */
	queueName?: string
}

export interface PbxAppEmployeesExtensionCallCallee {
	/** Callee phone number. */
	number: string
	/** Callee name, if available. */
	name?: string
}

export interface PbxAppEmployeesExtensionPhone {
	/** Id of the phone. */
	_id: string
	/** Type of phone: SIPPHONE or MOBILE. */
	type: 'SIPPHONE' | 'MOBILE' | 'EXTERNAL'
	/** Name of the phone (SIPPHONE) or number (MOBILE). */
	name: string
	/** Phone model - if available. */
	model?: string
	/** Phone model Id - if available. */
	modelId?: string
}

export interface PbxAppEmployeesPresence extends EmployeePresence {
	originalState: EmployeePresenceState
	originalMessage: string
}

// #endregion

// #region PBX App Misc

/** Response for /pbx/app/grouppermissions */
export interface PbxAppGroupPermissionsResponse {
	/** True if user may pickup calls from the target extension. */
	pickup: boolean
	/** True if user may steal ongoing calls from the target extension. */
	steal: boolean
	/** True if user may listen in on calls on the target extension. */
	spy: boolean
	/** True if user may set the presence for the target extensions employee. */
	setPresence: boolean
	/** True if user may add and remove the extension to/from a queue. */
	queueMember: boolean
	/** True if user may see who the extension is talking to. */
	viewOtherParty: boolean
	/** True if user may read the extensions employees calendar. */
	readCalendar: boolean
	/** True if user may write to the extensions employees calendar. */
	writeCalendar: boolean
	/** True if user may read the extensions employees phonebook. */
	readPhonebook: boolean
	/** True if user may write to the extensions employees phonebook. */
	writePhonebook: boolean
}

/** Names of group permissions */
export enum PbxAppGroupPermissionName {
	/** True if user may pickup calls from the target extension. */
	pickup = 'pickup',
	/** True if user may steal ongoing calls from the target extension. */
	steal = 'steal',
	/** True if user may listen in on calls on the target extension. */
	spy = 'spy',
	/** True if user may set the presence for the target extensions employee. */
	setPresence = 'setPresence',
	/** True if user may add and remove the extension to/from a queue. */
	queueMember = 'queueMember',
	/** True if user may see who the extension is talking to. */
	viewOtherParty = 'viewOtherParty',
	/** True if user may read the extensions employees calendar. */
	readCalendar = 'readCalendar',
	/** True if user may write to the extensions employees calendar. */
	writeCalendar = 'writeCalendar',
	/** True if user may read the extensions employees phonebook. */
	readPhonebook = 'readPhonebook',
	/** True if user may write to the extensions employees phonebook. */
	writePhonebook = 'writePhonebook'
}

/** Response for /pbx/app/permissions */
export type PbxAppPermissionsResponse = Record<string, PbxAppGroupPermissionsResponse>

/** Response for /pbx/app/reseller */
export interface PbxAppResellerResponse {
	/** Id of reseller */
	_id: string
	/** Name */
	name: string
	/** Phone number */
	phoneNumber: string
	/** Fax number */
	faxNumber: string
	/** Email address */
	emailAddress: string
	/** Website URL */
	website: string
	/** URL for the administration portal of the reseller */
	resellerPortal: string
	/** Address */
	address: {
		/** Street */
		address: string
		/** ZIP code */
		zip: string
		/** City */
		city: string
		/** Country code - ISO3166-2 */
		country: string
		/** State, if available */
		state?: string
		/** Danish municipality code */
		municipalityCode: number
		/** Alternative name for the address, if available. */
		alternativeName?: string
	}
}

// #endregion

// #region PBX App Queues

/** Response for /pbx/app/queues */
export type PbxAppQueuesResponse = PbxAppQueuesItem[]

/** A queue, part of /pbx/app/queues response */
export interface PbxAppQueuesItem {
	/** Id of queue. */
	_id: string
	/** Name of queue. */
	name: string
	/** Local extension number of the queue. */
	number?: string
	/** True if employee may join/leave the queue. */
	dynamic: boolean
	/** True if employee is a member of the queue. */
	member: boolean
	/** Number of members in the queue (all=false). */
	members: number
	/** Number of calls in the queue (all=false). */
	callers: number
	/** Max seconds within a call must be answered to uphold the service level. */
	serviceLevel: number
	/** Max seconds before a call is considered under critical service level. */
	serviceLevelCritical: number
}

/** Response for /pbx/app/queues?all=true */
export type PbxAppQueuesAllResponse = PbxAppQueuesAllItem[]

/** A queue, part of /pbx/app/queues?all=true */
export interface PbxAppQueuesAllItem {
	/** Id of queue. */
	_id: string
	/** Name of queue. */
	name: string
	/** Local extension number of the queue. */
	number?: string
	/** True if employee may join/leave the queue. */
	dynamic: boolean
	/** True if employee is a member of the queue. */
	member: boolean
	/** Max seconds within a call must be answered to uphold the service level. */
	serviceLevel: number
	/** Max seconds before a call is considered under critical service level. */
	serviceLevelCritical: number
	/** Priority of the queue in relation to other queues. Higher priority get access to available members first. */
	priority: number
	/** Members of the queue - only if all=true */
	members: PbxAppQueuesMember[]
	/** List of callers currently in the queue - only if all=true */
	callers: PbxAppQueuesCaller[]
}

export interface PbxAppQueuesMember {
	/** Unique id of the member. Used when removing member from the queue. */
	_id: string
	/** Id of a member extension, null if member is external. */
	extension?: string
	/** Phone number of external member. Null if member is extension. */
	number?: string
	/** Priority of member in the queue, 1-20, lower gets calls first. Defaults to 1. */
	priority: number
	/** Number of calls answered today by the member. */
	calls: number
	/** Number of seconds the member has been taking talking to queue calls today. */
	seconds: number
	/** Date and time the member handled the last call. */
	lastCallEnded?: Date
	/** If true, the member cannot leave the queue. It can only be done via API/Web by a MANAGER or higher. */
	permanent: boolean
}

export interface PbxAppQueuesCaller {
	/** Unique id of the call - channel-id-hostname. */
	_id: string
	/** State of caller */
	state: PbxAppQueuesCallerState
	/** Callers number. */
	number: string
	/** Callers name - if available. */
	name: string
	/** True if callers number is secret. */
	privacy: boolean
	/** Date and time when the caller entered the queue. */
	entered: Date
	/** Date and time when the caller was answered (or called back). */
	answered?: Date
	/** The number the caller called. Used as a-number when doing callback. */
	presentation?: string
	/** Extension id of member who answered the call. */
	answeredByExtension?: string
	/** Phone number of member who answered the call. */
	answeredByNumber?: string
	/** Session Id */
	sessionId: string

}

// #endregion

// #region Enums

export enum PbxAppSettingsIntegrationRunWhen {
	/* Do not use */
	NEVER = 'NEVER',
	/** Invoke when an inbound call is ringing */
	RINGING = 'RINGING',
	/** Invoke when an inbound call is answered */
	ANSWERED = 'ANSWERED'
}

export enum PbxAppSettingsIntegrationBrowserActionType {
	/** Sleep for x milliseconds */
	sleep = 'sleep',
	/** Select a DOM target for subsequent actions */
	target = 'target',
	/** Click on the target */
	click = 'click',
	/** Focus the target */
	focus = 'focus',
	/** Clear the target of type input/textarea */
	clear = 'clear',
	/** Insert text into the target */
	insert = 'insert',
	/** Execute the provided javascript */
	js = 'js',
	/** Scroll to top of the page */
	scrolltop = 'scrolltop'
}

export enum PbxAppDashboardItemType {
	/** My calls */
	MYCALLS = 'MYCALLS',
	/** Queue */
	QUEUE = 'QUEUE',
	/** Multiple queues */
	MULTIQUEUE = 'MULTIQUEUE',
	/** Colleagues/extensions */
	EXTENSIONS = 'EXTENSIONS',
	/** Web injection */
	WEB = 'WEB',
	/** Switches and variables */
	SWITCHVAR = 'SWITCHVAR',
	/** Shortcuts */
	SHORTCUTS = 'SHORTCUTS'
}

export enum PbxAppDashboardItemQueueStats {
	/** Calls lost due to no members */
	NOMEMBERS = 'NOMEMBERS',
	/** Calls lost due to all members busy */
	ALLBUSY = 'ALLBUSY',
	/** Calls lost due to timeout */
	TIMEOUT = 'TIMEOUT',
	/** Calls lost due to caller giving up */
	CANCELLED = 'CANCELLED',
	/** Calls answered */
	ANSWERED = 'ANSWERED',
	/** Call not answered */
	UNANSWERED = 'UNANSWERED',
	/** Total calls */
	TOTAL = 'TOTAL',
	/** Average wait time */
	AVERAGEWAITTIME = 'AVERAGEWAITTIME',
	/** Averiage conversation time */
	AVERAGETALKTIME = 'AVERAGETALKTIME',
	/** Total conversation time */
	TOTALTALKTIME = 'TOTALTALKTIME',
	/** Longest wait time */
	LONGESTWAITTIME = 'LONGESTWAITTIME',
	/** Most recent wait time */
	LASTWAITTIME = 'LASTWAITTIME',
	/** Service level exceeded */
	SERVICELEVEL = 'SERVICELEVEL',
	/** Critical service level exceeded */
	SERVICELEVELCRITICAL = 'SERVICELEVELCRITICAL'
}

export enum PbxAppDashboardItemWebMode {
	/** static URL */
	STATIC = 'STATIC',
	/** static URL that is auto-reloaded at an interval */
	RELOADED = 'RELOADED',
	/** URL is invoked and content shown when the phone rings */
	RINGING = 'RINGING',
	/** URL is invoked and content shown when the phone is answered */
	ANSWERED = 'ANSWERED',
	/** URL is invoked and content shown when the phone rings or an outbound call is made */
	RINGING_OR_OUT = 'RINGING_OR_OUT',
	/** URL is invoked and content shown when the phone is answered or an outbound call is answered */
	ANSWERED_OR_OUT = 'ANSWERED_OR_OUT'
}

export enum PbxAppDashboardItemWebClose {
	/** content is always shown */
	NEVER = 'NEVER',
	/** closes when call ends */
	ON_END = 'ON_END',
	/** a close button is added to the headline. */
	BUTTON = 'BUTTON'
}

export enum PbxAppEmployeesType {
	/** Record is for an employee */
	EMPLOYEE = 'EMPLOYEE',
	/** Record is for an extension without an employee */
	EXTENSION = 'EXTENSION'
}

export enum PbxAppEmployeesExtensionCallOriginator {
	/** Call originated by a SIP phone */
	SIPPHONE = 'SIPPHONE',
	/** Call originated by a mobile phone */
	MOBILE = 'MOBILE',
	/** Call originated by external entity */
	EXTERNAL = 'EXTERNAL',
	/** Call originated by system (pbx) */
	SYSTEM = 'SYSTEM'
}

export enum PbxAppQueuesCallerState {
	/** waiting to be answered */
	QUEUED = 'QUEUED',
	/** waiting to be answered but requires callback */
	CALLBACK = 'CALLBACK',
	/** VIP caller waiting to be answered, not included when calculating position in queue. */
	VIP = 'VIP',
	/** Call was answered by a member */
	ANSWERED = 'ANSWERED',
	/** Caller hungup before the call was answered */
	CANCELLED = 'CANCELLED',
	/** Caller timed out, transfer to another diaplan item was engaged */
	TIMED_OUT = 'TIMED_OUT',
	/** All members were busy, transfer to another dialplan item was engaged  */
	ALL_BUSY = 'ALL_BUSY',
	/** No members in the queue, transfer to another dialplan item was engaged */
	NO_MEMBERS = 'NO_MEMBERS'
}

// #endregion
